import React from "react";
import { motion } from "framer-motion";
import { ArrowDownRight, Download } from "lucide-react";
import { PROFILE, STATS } from "../../data";

export const Hero = () => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl section-x grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="label-caps text-berry mb-6"
          >
            Corporate Finance &amp; Accounting
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.98] text-navy"
          >
            Turning financial
            <br />
            data into
            <span className="italic text-berry"> decisions</span>.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-8 text-lg text-slate-600 max-w-xl leading-relaxed"
          >
            {PROFILE.bio}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-work-btn"
              onClick={() => scrollTo("work")}
              className="group bg-navy hover:bg-navy-800 text-white px-7 py-3.5 rounded-sm transition-colors duration-200 flex items-center gap-2 hover:scale-[1.03]"
            >
              View selected work
              <ArrowDownRight size={18} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-contact-btn"
              onClick={() => scrollTo("contact")}
              className="px-7 py-3.5 rounded-sm border border-slate-300 text-navy hover:border-berry hover:text-berry transition-colors duration-200"
            >
              Get in touch
            </button>
            <a
              href={PROFILE.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-resume-btn"
              className="group px-7 py-3.5 rounded-sm text-navy hover:text-berry transition-colors duration-200 flex items-center gap-2"
            >
              <Download size={18} className="transition-transform duration-200 group-hover:translate-y-0.5" />
              Download CV
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="absolute -top-5 -right-5 w-full h-full bg-navy rounded-sm hidden sm:block" />
          <div className="absolute -bottom-6 -left-6 w-28 h-28 bg-berry/90 rounded-sm hidden sm:block" />
          <div className="relative overflow-hidden rounded-sm">
            <img
              data-testid="hero-portrait"
              src={PROFILE.heroPortrait}
              alt="Modern financial district architecture"
              className="w-full h-[420px] lg:h-[520px] object-cover object-center"
            />
          </div>
        </motion.div>
      </div>

      <div className="mx-auto max-w-7xl section-x mt-16 lg:mt-24">
        <div className="grid grid-cols-1 sm:grid-cols-3 border-t border-slate-200">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="py-8 md:py-10 border-b md:border-b-0 md:border-r border-slate-200 last:border-r-0 md:pr-6 md:pl-0 pl-0 pr-6"
              data-testid={`stat-${i}`}
            >
              <div className="font-serif text-4xl lg:text-5xl text-navy">{s.value}</div>
              <div className="mt-2 text-sm text-slate-500">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
